import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Button } from "../components/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/Collapsible";
import { Counter } from "../components/Counter";
import { HoverScrollText } from "../components/HoverScrollText";
import { Icon } from "../components/Icon";
import { LinkWrapper } from "../components/LinkWrapper";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { TypingAnimation } from "../components/TypingAnimation";
import { Lock01 } from "../icons";
import { ChevronDown, ChevronRight, ChevronUp, DotsHorizontal } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
/**
 * A vertical, scrollable list of navigation entries for sidebars, composed
 * with NavigationListItem, NavigationListLabel / NavigationListCompactLabel,
 * and NavigationListCollapsibleSection. Use it for the primary sidebar
 * navigation of an app (conversations, projects, spaces); for breadcrumb-style
 * hierarchy or tabbed content switching, use Breadcrumbs or Tabs instead.
 * @summary Scrollable sidebar navigation list.
 */
const NavigationList = React.forwardRef(({
  className,
  children,
  viewportRef,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(ScrollArea, {
    ref: ref,
    viewportRef: viewportRef,
    className: className,
    ...props
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5"
  }, children), /*#__PURE__*/React.createElement(ScrollBar, null));
});
NavigationList.displayName = "NavigationList";

/**
 * Status of a navigation item, rendered at the end of the row: `idle` shows
 * nothing, `unread` a highlight dot, `blocked` an info dot, `error` a
 * warning dot, `generating` a spinner.
 */

/**
 * A single navigation entry: label with optional icon or avatar, selected
 * state, status dot, count badge, and a hover-revealed actions slot.
 * @summary Single sidebar navigation entry.
 */
const NavigationListItem = React.forwardRef(({
  className,
  selected,
  disabled,
  label,
  labelAnimation = "none",
  onTypingAnimationComplete,
  scrollLabelOnHover,
  icon,
  avatar,
  href,
  target,
  rel,
  replace,
  shallow,
  moreMenu,
  keepHoverOnMoreMenu,
  status = "idle",
  count,
  hasActivity,
  suffix,
  ...props
}, ref) => {
  const getStatusDotColor = () => {
    switch (status) {
      case "unread":
        return "h-2 w-2 m-1 bg-highlight-500";
      case "blocked":
        return "h-2 w-2 m-1 bg-info-400";
      case "error":
        return "h-2 w-2 m-1 bg-warning-400";
      default:
        return "";
    }
  };
  const shouldShowStatusDot = status !== "idle" && status !== "generating";
  const counterValue = count && count > 0 ? count : undefined;
  return /*#__PURE__*/React.createElement("div", {
    className: cn("group/menu-item relative",
    // HoverScrollText scrolls while an ancestor `group` is hovered.
    scrollLabelOnHover && "group", className),
    ref: ref,
    "data-nav": "menu-button",
    "data-selected": selected,
    "data-disabled": disabled,
    ...props
  }, /*#__PURE__*/React.createElement(LinkWrapper, {
    className: "group/nav-item focus-visible:outline-hidden",
    href: disabled ? undefined : href,
    target: target,
    rel: rel,
    replace: replace,
    shallow: shallow
  }, /*#__PURE__*/React.createElement("div", {
    "aria-disabled": disabled,
    className: cn("peer/menu-button", "text-muted-foreground font-medium", "box-border flex items-center w-full gap-1.5 cursor-pointer select-none", "items-center outline-hidden rounded-lg text-sm p-2 transition-colors duration-150 motion-reduce:transition-none", "group-focus-visible/nav-item:ring-2 group-focus-visible/nav-item:ring-inset group-focus-visible/nav-item:ring-ring", "data-[disabled]:pointer-events-none", !disabled && (keepHoverOnMoreMenu ? "group-hover/menu-item:bg-hover group-hover/menu-item:text-primary" : "hover:bg-hover hover:text-primary"), selected && "bg-selected text-primary", disabled && "pointer-events-none cursor-default opacity-50")
  }, (icon || disabled) && /*#__PURE__*/React.createElement(Icon, {
    visual: disabled ? Lock01 : icon,
    size: "xs",
    className: "m-0.5 text-muted-foreground"
  }), avatar, label && /*#__PURE__*/React.createElement("span", {
    className: cn("overflow-hidden text-ellipsis whitespace-nowrap", !suffix && "grow group-focus-within/menu-item:pr-8 group-hover/menu-item:pr-8 group-data-[selected=true]/menu-item:pr-8", hasActivity && "font-semibold")
  }, labelAnimation === "typing" ? /*#__PURE__*/React.createElement(TypingAnimation, {
    text: label,
    duration: 32,
    onComplete: onTypingAnimationComplete
  }) : scrollLabelOnHover ? /*#__PURE__*/React.createElement(HoverScrollText, {
    text: label
  }) : label), suffix && /*#__PURE__*/React.createElement("div", {
    className: cn("flex grow flex-shrink-0 items-center justify-end", moreMenu && "group-focus-within/menu-item:hidden group-hover/menu-item:hidden")
  }, suffix), counterValue !== undefined && /*#__PURE__*/React.createElement(Counter, {
    value: counterValue,
    size: "xs",
    variant: "outline",
    className: cn("flex-shrink-0 translate-x-0.5", moreMenu && "group-focus-within/menu-item:hidden group-hover/menu-item:hidden")
  }), status === "generating" && /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-shrink-0", moreMenu && "group-focus-within/menu-item:hidden group-hover/menu-item:hidden")
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "xs"
  })), shouldShowStatusDot && /*#__PURE__*/React.createElement("div", {
    className: cn("heading-xs flex flex-shrink-0 items-center justify-center rounded-full", moreMenu && "group-focus-within/menu-item:hidden group-hover/menu-item:hidden", getStatusDotColor())
  }))), moreMenu && /*#__PURE__*/React.createElement(React.Fragment, null, moreMenu));
});
NavigationListItem.displayName = "NavigationListItem";
/**
 * The hover-revealed "more" button anchored to an item's right edge,
 * typically used as the trigger inside a NavigationListItem's `moreMenu`.
 * @summary Hover-revealed per-item action trigger.
 */
const NavigationListItemAction = React.forwardRef(({
  className,
  forceVisible,
  ...props
}, ref) => {
  const {
    _: _t
  } = _useLingui();
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    "data-sidebar": "menu-action",
    className: cn("absolute right-2 top-1.5 transition-opacity", forceVisible ? "opacity-100" : "opacity-0 group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100", className),
    ...props
  }, /*#__PURE__*/React.createElement(Button, {
    size: "xmini",
    icon: DotsHorizontal,
    "aria-label": _t(
    /** i18n */
    {
      id: "JcD7qf",
      message: "More actions"
    }),
    variant: "ghost",
    className: "hover:bg-hover active:bg-selected"
  }));
});
NavigationListItemAction.displayName = "NavigationListItemAction";
/**
 * A group label for always-visible sections of a NavigationList, with an
 * optional icon, action slot, and sticky positioning.
 * @summary Group label for navigation sections.
 */
const NavigationListLabel = React.forwardRef(({
  className,
  label,
  icon,
  isSticky,
  action,
  ...props
}, ref) => /*#__PURE__*/React.createElement("div", {
  ref: ref,
  className: cn("flex items-center justify-between gap-2 pt-4 pb-2 px-2 whitespace-nowrap overflow-hidden text-ellipsis", "text-sm", "bg-app-background", "text-muted-foreground", isSticky && "sticky top-0 z-10 border-border", className),
  ...props
}, /*#__PURE__*/React.createElement("div", {
  className: "flex items-center gap-1 overflow-hidden text-ellipsis"
}, icon && /*#__PURE__*/React.createElement(Icon, {
  visual: icon,
  size: "xs"
}), /*#__PURE__*/React.createElement("span", {
  className: "overflow-hidden text-ellipsis"
}, label)), action));
NavigationListLabel.displayName = "NavigationListLabel";
/**
 * A small, uppercase group label variant for dense NavigationList sections.
 * @summary Compact uppercase group label.
 */
const NavigationListCompactLabel = React.forwardRef(({
  className,
  label,
  isSticky,
  ...props
}, ref) => /*#__PURE__*/React.createElement("div", {
  ref: ref,
  className: cn(
  // px-2 matches NavigationListItem's p-2 so the label's text aligns with
  // the item labels underneath it. The lopsided pt-4/pb-1 groups the label
  // with the items it heads, and breaks it away from the group above.
  "flex px-2 py-1 text-xs font-semibold text-faint pt-4 uppercase whitespace-nowrap overflow-hidden text-ellipsis", isSticky && "sticky top-0 z-10 bg-muted-background border-border", className),
  ...props
}, /*#__PURE__*/React.createElement("div", {
  className: "flex items-center gap-1 overflow-hidden text-ellipsis"
}, label)));
NavigationListCompactLabel.displayName = "NavigationListCompactLabel";
const collapseableStyles = cva(cn("text-left", "text-muted-foreground", "text-sm whitespace-nowrap overflow-hidden text-ellipsis", "select-none", "outline-hidden rounded-xl", "data-[disabled]:pointer-events-none"), {
  variants: {
    isCollapsible: {
      true: "cursor-pointer",
      false: ""
    },
    // Static headers fill the row; collapsible ones shrink-wrap so the
    // chevron sits right next to the label instead of at the row's edge.
    grow: {
      true: "w-full flex-1",
      false: "min-w-0"
    }
  },
  defaultVariants: {
    isCollapsible: false,
    grow: true
  }
});

/**
 * A labeled section of a NavigationList that can be collapsible
 * (`type="collapse"`) or always open (`type="static"`), with optional partial
 * collapse via `visibleItems` and a "Show all" overflow control. Use it for
 * sections users may want to expand/collapse; use NavigationListLabel for
 * plain always-visible grouping.
 * @summary Collapsible group of navigation items.
 */
const NavigationListCollapsibleSection = React.forwardRef(({
  label,
  icon,
  count,
  action,
  actionOnHover = true,
  children,
  className,
  type = "static",
  defaultOpen,
  open,
  onOpenChange,
  visibleItems,
  overflowCount,
  overflowHasActivity,
  ...props
}, ref) => {
  const {
    _: _t2
  } = _useLingui();
  const [isShowingAll, setIsShowingAll] = React.useState(false);
  const childArray = React.Children.toArray(children);
  const hasPartialCollapse = visibleItems !== undefined && visibleItems < childArray.length;
  const visibleChildrenSlice = hasPartialCollapse ? childArray.slice(0, visibleItems) : childArray;
  const overflowChildren = hasPartialCollapse ? childArray.slice(visibleItems) : [];
  const isCollapsible = type !== "static";
  const counterValue = count && count > 0 ? count : undefined;
  const labelElement = /*#__PURE__*/React.createElement("div", {
    className: cn("notranslate", collapseableStyles({
      isCollapsible,
      grow: !isCollapsible
    }),
    // The collapsible header row owns the text color (including its
    // hover state), so the label inherits it instead of forcing its own.
    isCollapsible && "text-inherit")
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-1.5"
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    visual: icon,
    size: "xs"
  }), /*#__PURE__*/React.createElement("span", {
    className: "overflow-hidden text-ellipsis"
  }, label), counterValue !== undefined && /*#__PURE__*/React.createElement(Counter, {
    value: counterValue,
    size: "xs",
    variant: "highlight"
  })));
  const actionElement = action && /*#__PURE__*/React.createElement("div", {
    // Lets the header row keep its active styling while a menu opened from
    // one of these actions is still open (see the collapse header below).
    "data-nav": "section-action",
    className: cn("flex gap-1 transition-opacity", actionOnHover ? cn("[@media(hover:hover)_and_(pointer:fine)]:opacity-0 hover:opacity-100 group-has-[:focus-visible]/menu-item:opacity-100 group-hover/menu-item:opacity-100",
    // The pointer leaves the row to navigate the menu it just
    // opened; keep the action visible until the menu closes.
    "has-[[data-state=open]]:opacity-100") : "opacity-100"),
    onClick: e => {
      e.stopPropagation();
    }
  }, action);
  const handleOpenChange = newOpen => {
    if (!newOpen) {
      setIsShowingAll(false);
    }
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(newOpen);
  };
  const renderedContent = /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5"
  }, visibleChildrenSlice, hasPartialCollapse && /*#__PURE__*/React.createElement(Collapsible, {
    open: isShowingAll,
    onOpenChange: setIsShowingAll
  }, /*#__PURE__*/React.createElement(CollapsibleContent, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5"
  }, overflowChildren)), /*#__PURE__*/React.createElement("div", {
    className: "px-1.5 py-1 gap-1 flex"
  }, isShowingAll ? /*#__PURE__*/React.createElement(Button, {
    size: "xs",
    icon: ChevronUp,
    variant: "ghost-secondary",
    label: _t2(
    /** i18n */
    {
      id: "vLyv1R",
      message: "Hide"
    }),
    onClick: () => setIsShowingAll(false)
  }) : /*#__PURE__*/React.createElement(Button, {
    size: "xs",
    icon: ChevronDown,
    variant: "ghost-secondary",
    label: _t2(
    /** i18n */
    {
      id: "lZw9AX",
      message: "Show all"
    }),
    isCounter: overflowCount !== undefined && overflowCount > 0,
    counterValue: String(overflowCount),
    className: overflowHasActivity ? "[&>div]:font-bold" : undefined,
    onClick: () => setIsShowingAll(true)
  }))));
  if (type === "static") {
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      className: className,
      ...props
    }, /*#__PURE__*/React.createElement("div", {
      className: "group/menu-item relative flex flex-1 items-center justify-start gap-2 pl-2 py-1.5 font-medium"
    }, labelElement, actionElement), renderedContent);
  }
  const collapsibleProps = {
    defaultOpen,
    open,
    onOpenChange: handleOpenChange,
    ...props
  };
  return /*#__PURE__*/React.createElement(Collapsible, {
    ref: ref,
    className: className,
    ...collapsibleProps
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("group/menu-item relative", "text-muted-foreground font-semibold",
    // No gap: it would be dead space between the trigger and the
    // action slot. The trigger's own pr-2 does the separating.
    "box-border flex items-center w-full select-none",
    // The row's own padding lives on the trigger below, so the whole
    // row height is clickable rather than just the label's line box.
    // pr-2 keeps the action slot off the right edge.
    "items-center outline-hidden rounded-lg text-sm pr-2 transition-colors duration-150 motion-reduce:transition-none", "hover:bg-hover hover:text-primary",
    // Hold the hover styling while a menu opened from one of the row's
    // actions is still open. Scoped to the action slot so the
    // CollapsibleTrigger's own data-state=open never matches.
    "has-[[data-nav=section-action]_[data-state=open]]:bg-hover has-[[data-nav=section-action]_[data-state=open]]:text-primary")
  }, /*#__PURE__*/React.createElement(CollapsibleTrigger, {
    hideChevron: true,
    className: "p-2"
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex min-w-0 items-center gap-1"
  }, labelElement, /*#__PURE__*/React.createElement(Icon, {
    visual: ChevronRight,
    size: "xs",
    className: "block shrink-0 group-data-[state=open]/col:hidden"
  }), /*#__PURE__*/React.createElement(Icon, {
    visual: ChevronDown,
    size: "xs",
    className: cn("hidden shrink-0 transition-opacity group-data-[state=open]/col:block", "[@media(hover:hover)_and_(pointer:fine)]:opacity-0", "group-hover/menu-item:opacity-100 group-has-[:focus-visible]/menu-item:opacity-100")
  }))), actionElement), /*#__PURE__*/React.createElement(CollapsibleContent, {
    animated: false,
    className: "data-[state=open]:overflow-visible"
  }, renderedContent));
});
NavigationListCollapsibleSection.displayName = "NavigationListCollapsibleSection";
export { NavigationList, NavigationListCollapsibleSection, NavigationListCompactLabel, NavigationListItem, NavigationListItemAction, NavigationListLabel };
//# sourceMappingURL=NavigationList.js.map